import { computed, onMounted, ref } from "vue";
import type { Collection, Pageable } from "bancojs";
import { useApiErrors } from "@/composables/useApiErrors";
import { useNotificationsStore } from "@/stores/notifications";
import { shallowCompare } from "@/utils/functions";

/**
 * Estado y comportamiento comunes a las vistas de índice.
 *
 * Las nueve vistas Index eran ~450 líneas cada una repitiendo lo mismo: cargar
 * una página, reaccionar a la paginación, un diálogo de filtro, un diálogo de
 * borrado con confirmación por texto, y el manejo de errores. Diferían sólo en
 * qué endpoint llaman, qué filtros ofrecen y qué texto pide el borrado.
 *
 * Extraerlo evita nueve copias que se desincronizan —cinco implementaciones
 * distintas de `getStateString` ya habían aparecido así— y reduce agregar una
 * vista a escribir configuración.
 *
 * Se usan `ref` y no un único `reactive`: con tipos genéricos, `reactive`
 * atraviesa `UnwrapRef` y pierde la relación entre `T`/`F` y el estado.
 */

export interface FilterOption<F extends string = string> {
  /** Nombre del query param que espera la API. */
  field: F;
  label: string;
}

export interface ConfirmSpec<T> {
  /** El texto exacto que el usuario debe escribir para habilitar el borrado. */
  expected: (item: T) => string;
  /** Etiqueta del campo, p. ej. "ID Usuario". */
  label: string;
  /** Frase de instrucción; admite <b> para resaltar. */
  hint: string;
  /** Mensaje cuando lo escrito no calza. */
  error: string;
  /** Advertencia sobre las consecuencias del borrado. */
  warning: (item: T) => string;
}

export interface IndexViewOptions<T, F extends string> {
  /** Título del encabezado y de las notificaciones. */
  title: string;
  /** Campo identificador; también es el orden por omisión. */
  rowKey: string;
  defaultSort?: string;
  size?: number;
  /** Consulta paginada. */
  fetchPage: (params: Record<string, unknown>) => Promise<Collection<T>>;
  /** Si se omite, la vista no ofrece borrado. */
  remove?: (item: T) => Promise<void>;
  filters?: ReadonlyArray<FilterOption<F>>;
  confirm?: ConfirmSpec<T>;
  /** Mensaje de éxito tras borrar. */
  removedMessage?: (item: T) => string;
}

export function useIndexView<T, F extends string = string>(
  options: IndexViewOptions<T, F>
) {
  const { report } = useApiErrors();
  const notifications = useNotificationsStore();

  const filters = ref({}) as import("vue").Ref<Partial<Record<F, string>>>;
  const loading = ref(true);
  const selected = ref(null) as import("vue").Ref<T | null>;
  const data = ref([]) as import("vue").Ref<T[]>;
  const totalElements = ref(0);
  const pagination = ref<Pageable>({
    size: options.size ?? 10,
    page: 0,
    sort: options.defaultSort ?? `${options.rowKey},asc`,
  });

  const removing = ref(false);
  const confirmText = ref("");

  const filterOpen = ref(false);
  const filterOption = ref(null) as import("vue").Ref<FilterOption<F> | null>;
  const filterValue = ref("");

  const activeFilters = computed(() =>
    (Object.keys(filters.value) as F[]).map((field) => ({
      field,
      label: options.filters?.find((o) => o.field === field)?.label ?? field,
      value: filters.value[field] as string,
    }))
  );

  async function loadData() {
    loading.value = true;
    try {
      const page = await options.fetchPage({
        ...filters.value,
        ...pagination.value,
      });
      totalElements.value = page.totalElements;
      data.value = page.content;
    } catch (err) {
      report(err, {
        title: options.title,
        fallback: "Ocurrió un error al recuperar la lista.",
      });
    } finally {
      loading.value = false;
    }
  }

  async function onPaginationChange(value: Pageable) {
    if (
      shallowCompare(
        value as unknown as Record<string, unknown>,
        pagination.value as unknown as Record<string, unknown>
      )
    ) {
      return;
    }
    pagination.value = { ...pagination.value, ...value };
    await loadData();
  }

  // ---- filtros ----------------------------------------------------------

  function openFilterDialog(option: FilterOption<F>) {
    filterOption.value = option;
    filterValue.value = filters.value[option.field] ?? "";
    filterOpen.value = true;
  }

  function closeFilterDialog() {
    filterOpen.value = false;
    filterOption.value = null;
    filterValue.value = "";
  }

  async function applyFilter() {
    if (!filterOption.value || !filterValue.value) return;
    filters.value = { ...filters.value, [filterOption.value.field]: filterValue.value };
    closeFilterDialog();
    // Los resultados filtrados son otro conjunto: volver a la primera página.
    pagination.value = { ...pagination.value, page: 0 };
    await loadData();
  }

  async function removeFilter(field: F) {
    const next = { ...filters.value };
    delete next[field];
    filters.value = next;
    pagination.value = { ...pagination.value, page: 0 };
    await loadData();
  }

  // ---- borrado ----------------------------------------------------------

  function openRemove(item: T) {
    selected.value = item;
    confirmText.value = "";
  }

  function closeRemove() {
    selected.value = null;
    confirmText.value = "";
  }

  const confirmMatches = computed(() => {
    if (!selected.value || !options.confirm) return false;
    return confirmText.value.trim() === options.confirm.expected(selected.value);
  });

  const confirmError = computed(() =>
    confirmText.value && !confirmMatches.value && options.confirm
      ? [options.confirm.error]
      : []
  );

  async function confirmRemove() {
    const target = selected.value;
    if (!target || !confirmMatches.value || !options.remove) return;
    removing.value = true;
    try {
      await options.remove(target);
      closeRemove();
      notifications.info(
        options.removedMessage?.(target) ?? "Registro eliminado.",
        "Borrado"
      );
      await loadData();
    } catch (err) {
      report(err, {
        title: "Borrado",
        fallback: "Ocurrió un error al eliminar el registro.",
      });
    } finally {
      removing.value = false;
    }
  }

  onMounted(loadData);

  return {
    options,
    filters,
    loading,
    selected,
    data,
    totalElements,
    pagination,
    removing,
    confirmText,
    filterOpen,
    filterOption,
    filterValue,
    activeFilters,
    confirmMatches,
    confirmError,
    loadData,
    onPaginationChange,
    openFilterDialog,
    closeFilterDialog,
    applyFilter,
    removeFilter,
    openRemove,
    closeRemove,
    confirmRemove,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type IndexView<T = any, F extends string = string> = ReturnType<
  typeof useIndexView<T, F>
>;
